import { useNavigate } from 'react-router-dom';
import { MapPin, Calendar, Lock, CheckCircle, Clock, Edit, Share2, Image, Flag } from 'lucide-react';

interface Campaign {
  id: string;
  title: string;
  description: string;
  deadline: string;
  location: { city: string; state: string };
  privacyMode: 'public' | 'private';
  status: 'active' | 'expired' | 'completed' | 'cancelled';
  completionMessage?: string;
  progressPercentage?: number;
  totalProducts?: number;
  fullyCommittedProducts?: number;
  coverImageUrl?: string;
  isVerified?: boolean;
}

interface CampaignCardProps {
  campaign: Campaign;
  onShare: (campaign: Campaign) => void;
  onEdit: (campaign: Campaign) => void;
  onCover: (campaign: Campaign) => void;
  onComplete?: (campaign: Campaign) => void;
}

export function CampaignCard({ campaign, onShare, onEdit, onCover, onComplete }: CampaignCardProps) {
  const navigate = useNavigate();

  const isExpired = new Date(campaign.deadline) < new Date();
  // Normalize to local midnight for accurate day counting
  const deadlineLocal = new Date(campaign.deadline);
  const todayLocal = new Date();
  deadlineLocal.setHours(0, 0, 0, 0);
  todayLocal.setHours(0, 0, 0, 0);
  const diffDays = Math.round((deadlineLocal.getTime() - todayLocal.getTime()) / (1000 * 60 * 60 * 24));
  const isDeadlineSoon = diffDays <= 7 && diffDays > 0;
  const progress = campaign.progressPercentage || 0;

  const formatDate = (d: string) => new Date(d).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });

  return (
    <div className="bg-white rounded-xl border border-gray-100 hover:border-indigo-200 hover:shadow-lg transition-all overflow-hidden group">
      {/* Cover image or gradient */}
      <div className="relative h-28">
        {campaign.coverImageUrl ? (
          <img src={campaign.coverImageUrl} alt={campaign.title} loading="lazy" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500" />
        )}
        {/* Badges */}
        <div className="absolute top-2.5 left-2.5 flex flex-wrap gap-1">
          {campaign.isVerified && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-500/90 text-white">
              <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> Verificada
            </span>
          )}
          {campaign.privacyMode === 'private' && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-black/50 text-white backdrop-blur-sm">
              <Lock className="w-2.5 h-2.5 mr-0.5" /> Privada
            </span>
          )}
          {campaign.status === 'completed' && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-500/90 text-white">
              <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> Completada
            </span>
          )}
          {isExpired && campaign.status === 'active' && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-500/90 text-white">
              Expirada
            </span>
          )}
          {campaign.status === 'expired' && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-500/90 text-white">
              <Clock className="w-2.5 h-2.5 mr-0.5" /> Vencida
            </span>
          )}
          {isDeadlineSoon && !isExpired && campaign.status === 'active' && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/90 text-white">
              <Clock className="w-2.5 h-2.5 mr-0.5" /> {diffDays}d
            </span>
          )}
        </div>

        {/* Progress ring */}
        <div className="absolute top-2.5 right-2.5">
          <div className="relative w-11 h-11">
            <svg className="w-11 h-11 -rotate-90" viewBox="0 0 44 44">
              <circle cx="22" cy="22" r="18" stroke="rgba(255,255,255,0.3)" strokeWidth="3" fill="none" />
              <circle cx="22" cy="22" r="18" stroke="white" strokeWidth="3" fill="none"
                strokeDasharray={`${2 * Math.PI * 18}`}
                strokeDashoffset={`${2 * Math.PI * 18 * (1 - progress / 100)}`}
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-white text-[10px] font-bold">
              {Math.round(progress)}%
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-sm font-semibold text-gray-900 line-clamp-1 mb-1">{campaign.title}</h3>
        <p className="text-xs text-gray-500 line-clamp-2 mb-3">{campaign.description}</p>

        <div className="flex items-center justify-between text-[11px] text-gray-400 mb-3">
          <span className="inline-flex items-center">
            <MapPin className="w-3 h-3 mr-0.5" /> {campaign.location.city}
          </span>
          <span className="inline-flex items-center">
            <Calendar className="w-3 h-3 mr-0.5" /> {formatDate(campaign.deadline)}
          </span>
        </div>

        {/* Progress bar */}
        <div className="mb-3">
          <div className="w-full bg-gray-100 rounded-full h-1.5">
            <div className="h-1.5 rounded-full bg-indigo-500 transition-all" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-[10px] text-gray-400 mt-1 text-right">
            {campaign.fullyCommittedProducts || 0}/{campaign.totalProducts || 0} productos
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-gray-50">
          <button
            onClick={() => navigate(`/campaigns/${campaign.id}`)}
            className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors text-center"
          >
            Ver detalles
          </button>
          {campaign.status === 'active' && (
            <button
              onClick={() => onEdit(campaign)}
              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
              title="Editar"
            >
              <Edit className="w-4 h-4" />
            </button>
          )}
          {campaign.status === 'active' && (
            <button
              onClick={() => onCover(campaign)}
              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
              title="Portada"
            >
              <Image className="w-4 h-4" />
            </button>
          )}
          {(campaign.status === 'active' || campaign.status === 'expired') && onComplete && (
            <button
              onClick={() => onComplete(campaign)}
              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title="Finalizar campaña"
            >
              <Flag className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => onShare(campaign)}
            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            title="Compartir"
          >
            <Share2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
